import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { meetingAPI, chatAPI, usersAPI } from '../services/api';

// ── Identity — read from sessionStorage (set by App.js on every login) ────────
// Never parse the JWT header/payload manually when sessionStorage already has it.

// Read identity from sessionStorage keys that App.js sets on every login.
// This is the single source of truth — avoids double-decoding the JWT.
const getIdentity = () => {
  try {
    const tok = sessionStorage.getItem('gs_token');
    if (!tok) return { username: '', displayName: 'Me', isAdmin: false };

    // Our JWT is 2-part: base64url(payload).signature
    const payload = JSON.parse(atob(tok.split('.')[0].replace(/-/g, '+').replace(/_/g, '/')));

    const username    = (payload.user || '').toLowerCase();
    // Prefer full_name stored in sessionStorage (set by App.js on login)
    const displayName =
      sessionStorage.getItem('gs_staff_full_name') ||
      payload.full_name ||
      payload.school_name ||
      payload.user ||
      'Me';

    // Admin check: either old role='admin' JWT or new allowed_tabs containing all core tabs
    const storedTabs = (() => {
      try { return JSON.parse(sessionStorage.getItem('gs_allowed_tabs') || '[]'); }
      catch { return []; }
    })();
    const CORE_TABS = ['dashboard', 'students', 'reports', 'daybook', 'exams', 'operations'];
    const isAdmin = payload.role === 'admin' ||
      (Array.isArray(storedTabs) && CORE_TABS.every((t) => storedTabs.includes(t))) ||
      (Array.isArray(payload.allowed_tabs) && CORE_TABS.every((t) => payload.allowed_tabs.includes(t)));

    return { username, displayName, isAdmin };
  } catch { return { username: '', displayName: 'Me', isAdmin: false }; }
};

// ── WebRTC ────────────────────────────────────────────────────────────────────
const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

// ── Chat thread for the meeting room ─────────────────────────────────────────
const THREAD_KEY   = 'mtg:room';
const THREAD_TITLE = 'Meeting Room';

// ── Colour palette for participant tiles ─────────────────────────────────────
const TILE_COLORS = [
  '#4f46e5','#0891b2','#059669','#d97706','#dc2626',
  '#7c3aed','#db2777','#2563eb','#16a34a','#ea580c',
];
const tileColor = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return TILE_COLORS[Math.abs(h) % TILE_COLORS.length];
};
const initialsOf = (name = '') =>
  name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?';

const fmtTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};
const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString([], {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
};

// ── SVG Icons ─────────────────────────────────────────────────────────────────
const MicIcon = ({ off, size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {off ? (
      <>
        <line x1="1" y1="1" x2="23" y2="23"/>
        <path d="M9 9v3a3 3 0 005.12 2.12M15 9.34V4a3 3 0 00-5.94-.6"/>
        <path d="M17 16.95A7 7 0 015 12v-2m14 0v2a7 7 0 01-.11 1.23"/>
        <line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
      </>
    ) : (
      <>
        <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/>
        <path d="M19 10v2a7 7 0 01-14 0v-2"/>
        <line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
      </>
    )}
  </svg>
);

const VideoIcon = ({ off, size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {off ? (
      <>
        <path d="M16 16v1a2 2 0 01-2 2H3a2 2 0 01-2-2V7a2 2 0 012-2h2"/>
        <polygon points="23 7 16 12 23 17 23 7"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
      </>
    ) : (
      <>
        <polygon points="23 7 16 12 23 17 23 7"/>
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
      </>
    )}
  </svg>
);

const PhoneOffIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.68 13.31a16 16 0 003.41 2.6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7 2 2 0 011.72 2v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.42 19.42 0 013.43 8.94 19.79 19.79 0 01.36.27 2 2 0 012.34 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.32 7.91"/>
    <line x1="23" y1="1" x2="1" y2="23"/>
  </svg>
);

const ChatBubbleIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
  </svg>
);

const PeopleIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
  </svg>
);

const BellIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 01-3.46 0"/>
  </svg>
);

const PhoneCallIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.07 1.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.15 7.76a16 16 0 006.09 6.09l1.12-1.12a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
  </svg>
);

const CalendarIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const SendIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/>
    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);

const ScreenShareIcon = ({ active, size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {active ? (
      <>
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
        <path d="M8 21h8M12 17v4"/>
        <line x1="2" y1="8" x2="22" y2="8"/>
      </>
    ) : (
      <>
        <path d="M13 3H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2v-3"/>
        <polyline points="8 21 12 17 16 21"/>
        <line x1="12" y1="17" x2="12" y2="3"/>
        <polyline points="16 7 20 3 16 3 20 7"/>
      </>
    )}
  </svg>
);

// ── Live clock ─────────────────────────────────────────────────────────────────
function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const hhmm = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  const date = now.toLocaleDateString([], { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });
  return (
    <div style={{ textAlign: 'center', lineHeight: 1.2 }}>
      <div style={{ fontSize: '2.6rem', fontWeight: 800, color: '#fff', letterSpacing: -1, fontVariantNumeric: 'tabular-nums' }}>
        {hhmm}
      </div>
      <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)', fontWeight: 500, marginTop: 3, letterSpacing: '0.3px' }}>
        {date}
      </div>
    </div>
  );
}

// ── Participant tile ───────────────────────────────────────────────────────────
function ParticipantTile({ name, isMe, isMuted, speaking, videoStream }) {
  const color = tileColor(name);
  const videoRef = useRef(null);
  useEffect(() => {
    if (videoRef.current && videoStream) videoRef.current.srcObject = videoStream;
  }, [videoStream]);

  return (
    <div style={{
      position: 'relative',
      background: '#1a1a2e',
      borderRadius: 14,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      minHeight: 170, flex: '1 1 210px', maxWidth: 330,
      border: speaking ? '2px solid #22c55e' : '2px solid rgba(255,255,255,0.07)',
      transition: 'border-color 0.25s',
      overflow: 'hidden',
      boxShadow: speaking ? '0 0 0 4px rgba(34,197,94,0.15)' : '0 2px 12px rgba(0,0,0,0.3)',
    }}>
      {videoStream ? (
        <video
          ref={videoRef} autoPlay muted playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0, borderRadius: 12 }}
        />
      ) : (
        <div style={{
          width: 76, height: 76, borderRadius: '50%',
          background: color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 28, fontWeight: 800, color: '#fff', letterSpacing: 1,
          boxShadow: speaking ? `0 0 0 5px rgba(34,197,94,0.35)` : `0 0 0 3px rgba(255,255,255,0.08)`,
          transition: 'box-shadow 0.25s',
        }}>
          {initialsOf(name)}
        </div>
      )}
      {/* Name bar */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
        padding: '18px 10px 8px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        zIndex: 2,
      }}>
        <span style={{ fontSize: 13, color: '#fff', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}{isMe ? ' (You)' : ''}
        </span>
        {isMuted && (
          <span style={{ color: '#f87171', flexShrink: 0, marginLeft: 6 }}>
            <MicIcon off size={14} />
          </span>
        )}
      </div>
      {/* Speaking ring overlay */}
      {speaking && (
        <div style={{
          position: 'absolute', inset: 0, borderRadius: 12,
          border: '2px solid #22c55e',
          pointerEvents: 'none', zIndex: 3,
        }} />
      )}
    </div>
  );
}

// ── Chat message bubble ────────────────────────────────────────────────────────
function ChatBubble({ m, meUsername }) {
  const mine = (m.sender_username || '').toLowerCase() === meUsername.toLowerCase();
  const parts = m.content.split(/(@\w+)/g);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
      {!mine && (
        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3, paddingLeft: 4, fontWeight: 600 }}>
          {m.sender_name || m.sender_username}
        </div>
      )}
      <div style={{
        maxWidth: '84%', padding: '9px 13px',
        borderRadius: mine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
        background: mine ? 'linear-gradient(135deg,#4f46e5,#6366f1)' : '#252538',
        color: '#f1f5f9', fontSize: 13.5,
        wordBreak: 'break-word', whiteSpace: 'pre-wrap',
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
      }}>
        {parts.map((p, i) =>
          p.startsWith('@')
            ? <span key={i} style={{ color: '#a5b4fc', fontWeight: 700 }}>{p}</span>
            : p
        )}
        <div style={{ fontSize: 10, color: mine ? 'rgba(255,255,255,0.5)' : '#64748b', marginTop: 5, textAlign: 'right' }}>
          {fmtTime(m.created_at)}
        </div>
      </div>
    </div>
  );
}

// ── Toolbar button ─────────────────────────────────────────────────────────────
function ToolbarBtn({ icon, label, onClick, active, danger, badge, title, disabled }) {
  const bg  = danger ? 'rgba(220,38,38,0.2)' : active ? 'rgba(79,70,229,0.3)' : 'rgba(255,255,255,0.08)';
  const col = danger ? '#f87171' : active ? '#a5b4fc' : '#94a3b8';
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 4, width: 68, height: 58, borderRadius: 12, border: 'none',
        background: hover
          ? (danger ? 'rgba(220,38,38,0.35)' : active ? 'rgba(79,70,229,0.45)' : 'rgba(255,255,255,0.14)')
          : bg,
        color: col, cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 11, fontWeight: 600,
        transition: 'background 0.15s, color 0.15s',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {icon}
      <span>{label}</span>
      {badge > 0 && (
        <span style={{
          position: 'absolute', top: 6, right: 8,
          background: '#ef4444', color: '#fff',
          borderRadius: 10, minWidth: 18, height: 18,
          fontSize: 10, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 4px',
        }}>
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}

// ── Panel header ───────────────────────────────────────────────────────────────
function PanelHeader({ title, onClose }) {
  return (
    <div style={{
      height: 54, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0,
    }}>
      <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 14 }}>{title}</span>
      <button
        onClick={onClose}
        style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 4, borderRadius: 6, transition: 'color 0.15s' }}
        onMouseEnter={(e) => { e.currentTarget.style.color = '#e2e8f0'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = '#64748b'; }}
        title="Close"
      >✕</button>
    </div>
  );
}

// ── Staff row (People panel) ───────────────────────────────────────────────────
function StaffCallRow({ staff, inRoom, sent, onCall }) {
  const [hovered, setHovered] = useState(false);
  const name = staff.full_name || staff.username;
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '9px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        background: hovered ? 'rgba(255,255,255,0.04)' : 'transparent',
        transition: 'background 0.15s', cursor: 'default',
      }}
    >
      <div style={{
        position: 'relative', width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
        background: tileColor(name),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, fontWeight: 700, color: '#fff',
      }}>
        {initialsOf(name)}
        {inRoom && (
          <span style={{
            position: 'absolute', bottom: 0, right: 0,
            width: 11, height: 11, borderRadius: '50%',
            background: '#22c55e', border: '2px solid #13131f',
          }} />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </div>
        <div style={{ fontSize: 11, color: inRoom ? '#22c55e' : '#475569', marginTop: 1 }}>
          {inRoom ? '● In this call' : (staff.email || `@${staff.username}`)}
        </div>
      </div>
      {(hovered || sent) && !inRoom && (
        <button
          onClick={onCall}
          disabled={!!sent}
          title={sent ? 'Notification sent' : `Call ${name}`}
          style={{
            flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 32, height: 32, borderRadius: '50%', border: 'none',
            background: sent ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.15)',
            color: sent ? '#22c55e' : '#4ade80',
            cursor: sent ? 'default' : 'pointer',
            transition: 'background 0.15s, transform 0.1s',
            transform: hovered && !sent ? 'scale(1.08)' : 'scale(1)',
          }}
          onMouseEnter={(e) => { if (!sent) e.currentTarget.style.background = 'rgba(34,197,94,0.3)'; }}
          onMouseLeave={(e) => { if (!sent) e.currentTarget.style.background = 'rgba(34,197,94,0.15)'; }}
        >
          {sent
            ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            : <PhoneCallIcon size={14} />
          }
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HOME SCREEN
// ─────────────────────────────────────────────────────────────────────────────
function HomeScreen({ onNewMeeting, joining, error, identity }) {
  const { isAdmin } = identity;

  const [meetingTitle,  setMeetingTitle]  = useState('');
  const [meetingWith,   setMeetingWith]   = useState('all_staff');
  const [wantSchedule,  setWantSchedule]  = useState(false);
  const [msgText,       setMsgText]       = useState('');
  const [activeTab,     setActiveTab]     = useState('all');

  const [staffList,     setStaffList]     = useState([]);
  const [schedules,     setSchedules]     = useState([]);
  const [schedLoading,  setSchedLoading]  = useState(true);

  const [showSchedule,  setShowSchedule]  = useState(false);
  const [schedSaving,   setSchedSaving]   = useState(false);
  const [schedForm,     setSchedForm]     = useState({ title: '', date: '', time: '', note: '' });
  const [invitedUsers,  setInvitedUsers]  = useState([]);

  const [showNotifs,    setShowNotifs]    = useState(false);
  const [notifs,        setNotifs]        = useState([]);
  const [notifsLoaded,  setNotifsLoaded]  = useState(false);

  const [titleErr,      setTitleErr]      = useState('');

  const notifRef = useRef(null);

  // Load staff for "Meeting With" picker
  useEffect(() => {
    meetingAPI.getStaff()
      .then((r) => setStaffList(r.data?.data || []))
      .catch(() => {});
  }, []);

  // Load schedules
  useEffect(() => {
    meetingAPI.getSchedules()
      .then((r) => setSchedules(r.data?.data || []))
      .catch(() => {})
      .finally(() => setSchedLoading(false));
  }, []);

  // Poll notifications every 10 s
  useEffect(() => {
    const fetch = () =>
      meetingAPI.getNotifications()
        .then((r) => { setNotifs(r.data?.data || []); setNotifsLoaded(true); })
        .catch(() => setNotifsLoaded(true));
    fetch();
    const t = setInterval(fetch, 10000);
    return () => clearInterval(t);
  }, []);

  // Close notif dropdown on outside click
  useEffect(() => {
    if (!showNotifs) return;
    const h = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showNotifs]);

  const unreadCount = useMemo(() => notifs.filter((n) => !n.is_read).length, [notifs]);

  const markRead = (id) => {
    setNotifs((prev) => prev.map((n) => n.notif_id === id ? { ...n, is_read: true } : n));
    meetingAPI.markNotificationRead(id).catch(() => {});
  };
  const markAllRead = () => notifs.filter((n) => !n.is_read).forEach((n) => markRead(n.notif_id));

  const toggleInvite = (username) => {
    setInvitedUsers((prev) =>
      prev.includes(username) ? prev.filter((u) => u !== username) : [...prev, username]
    );
  };

  const saveSchedule = async () => {
    if (!schedForm.title || !schedForm.date || !schedForm.time) return;
    setSchedSaving(true);
    try {
      const scheduled_at = new Date(`${schedForm.date}T${schedForm.time}`).toISOString();
      const r = await meetingAPI.createSchedule({
        title: schedForm.title,
        scheduled_at,
        note: schedForm.note,
        invited_usernames: invitedUsers,
      });
      setSchedules((prev) => [...prev, r.data.data].sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)));
      setSchedForm({ title: '', date: '', time: '', note: '' });
      setInvitedUsers([]);
      setShowSchedule(false);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save schedule');
    } finally {
      setSchedSaving(false);
    }
  };

  const deleteSchedule = async (id) => {
    if (!window.confirm('Delete this scheduled meeting?')) return;
    try {
      await meetingAPI.deleteSchedule(id);
      setSchedules((prev) => prev.filter((s) => s.schedule_id !== id));
    } catch { alert('Failed to delete schedule'); }
  };

  const handleStartMeeting = (title) => {
    const t = (title || meetingTitle).trim();
    if (!t) { setTitleErr('Please enter a meeting title before starting.'); return; }
    setTitleErr('');
    onNewMeeting({ title: t, meetingWith, message: msgText });
  };

  const now = new Date();
  const todayStr    = now.toDateString();
  const tomorrowStr = new Date(now.getTime() + 86400000).toDateString();

  const filterSchedules = (tab) => {
    if (tab === 'today')    return schedules.filter((s) => new Date(s.scheduled_at).toDateString() === todayStr);
    if (tab === 'tomorrow') return schedules.filter((s) => new Date(s.scheduled_at).toDateString() === tomorrowStr);
    if (tab === 'hosted')   return schedules.filter((s) => !!s.created_by_me);
    if (tab === 'invited')  return schedules.filter((s) => !s.created_by_me);
    return schedules;
  };
  const displayedSchedules = filterSchedules(activeTab);

  const TABS = [
    { key: 'all',      label: 'All' },
    { key: 'today',    label: 'Today' },
    { key: 'tomorrow', label: 'Tomorrow' },
    { key: 'hosted',   label: 'My Meetings' },
    { key: 'invited',  label: 'Invited' },
  ];

  // Shared input style
  const inp = {
    width: '100%', padding: '11px 14px', borderRadius: 8,
    border: '1.5px solid #e2e8f0', background: '#fff',
    color: '#1e293b', fontSize: 14, outline: 'none',
    boxSizing: 'border-box', fontFamily: 'inherit',
    transition: 'border-color 0.15s',
  };
  const floatLabel = {
    position: 'absolute', top: -9, left: 10,
    background: '#fff', padding: '0 4px',
    fontSize: 11, fontWeight: 700, color: '#6366f1', letterSpacing: '0.3px',
  };
  const modalOverlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
    backdropFilter: 'blur(4px)',
  };
  const modalBox = {
    background: '#fff', borderRadius: 16, padding: '28px 32px',
    minWidth: 340, maxWidth: 480, width: '100%',
    display: 'flex', flexDirection: 'column', gap: 18,
    boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
  };
  const canSave = schedForm.title && schedForm.date && schedForm.time && !schedSaving;

  return (
    <div style={{
      minHeight: 'calc(100vh - 120px)',
      background: '#f1f2f6',
      borderRadius: 12, overflow: 'hidden',
      display: 'flex', flexDirection: 'row', gap: 0,
      fontFamily: 'inherit',
    }}>

      {/* ── LEFT PANEL ─────────────────────────────────────────── */}
      <div style={{
        width: 380, minWidth: 320, flexShrink: 0,
        background: '#fff',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center',
        padding: '32px 28px 28px',
        gap: 20,
        borderRight: '1px solid #e8eaf0',
        overflowY: 'auto',
      }}>

        {/* Host Meeting button */}
        <button
          onClick={() => handleStartMeeting()}
          disabled={joining}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
            color: '#fff', border: 'none', borderRadius: 50,
            padding: '11px 28px', fontSize: 14, fontWeight: 700,
            cursor: joining ? 'not-allowed' : 'pointer',
            boxShadow: '0 4px 16px rgba(220,38,38,0.35)',
            transition: 'transform 0.13s, box-shadow 0.13s',
            opacity: joining ? 0.7 : 1,
            alignSelf: 'center',
          }}
          onMouseEnter={(e) => { if (!joining) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(220,38,38,0.45)'; }}}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(220,38,38,0.35)'; }}
        >
          <VideoIcon off={false} size={22} />
          {joining ? 'Connecting…' : 'Host Meeting'}
        </button>

        {/* Meeting Title */}
        <div style={{ position: 'relative', width: '100%' }}>
          <span style={floatLabel}>Meeting Title*</span>
          <input
            value={meetingTitle}
            onChange={(e) => { setMeetingTitle(e.target.value); setTitleErr(''); }}
            placeholder="Meeting title"
            style={{ ...inp, borderColor: titleErr ? '#ef4444' : '#e2e8f0' }}
            onFocus={(e) => { e.currentTarget.style.borderColor = '#6366f1'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = titleErr ? '#ef4444' : '#e2e8f0'; }}
          />
          {titleErr && (
            <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>{titleErr}</div>
          )}
        </div>

        {/* Meeting With */}
        <div style={{ position: 'relative', width: '100%' }}>
          <span style={floatLabel}>Meeting With*</span>
          <select
            value={meetingWith}
            onChange={(e) => setMeetingWith(e.target.value)}
            style={{
              ...inp,
              appearance: 'none', paddingRight: 36,
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 12px center',
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = '#6366f1'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
          >
            <option value="all_staff">All Staff</option>
            {staffList.map((s) => (
              <option key={s.username} value={s.username}>{s.full_name || s.username}</option>
            ))}
          </select>
        </div>

        {/* Schedule checkbox — admin only */}
        {isAdmin && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, alignSelf: 'flex-start', cursor: 'pointer', fontSize: 13.5, color: '#374151' }}>
            <input
              type="checkbox"
              checked={wantSchedule}
              onChange={(e) => {
                setWantSchedule(e.target.checked);
                if (e.target.checked) {
                  setSchedForm({ title: meetingTitle || '', date: '', time: '', note: msgText });
                  setInvitedUsers([]);
                  setShowSchedule(true);
                }
              }}
              style={{ width: 15, height: 15, accentColor: '#6366f1', cursor: 'pointer' }}
            />
            I want to schedule this meeting.
          </label>
        )}

        {/* Message textarea */}
        <div style={{ position: 'relative', width: '100%' }}>
          <textarea
            value={msgText}
            onChange={(e) => setMsgText(e.target.value)}
            placeholder="Write your Message here, if any.."
            maxLength={244}
            rows={5}
            style={{
              ...inp,
              resize: 'vertical', minHeight: 100,
              fontFamily: 'inherit',
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = '#6366f1'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
          />
          <div style={{ textAlign: 'right', fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
            MAX {244 - msgText.length} Character{244 - msgText.length !== 1 ? 's' : ''}
          </div>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 13, width: '100%', boxSizing: 'border-box' }}>
            {error}
          </div>
        )}

        {/* Create & Join button */}
        <button
          onClick={() => handleStartMeeting()}
          disabled={joining}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
            color: '#fff', border: 'none', borderRadius: 50,
            padding: '11px 28px', fontSize: 14, fontWeight: 700,
            cursor: joining ? 'not-allowed' : 'pointer',
            boxShadow: '0 4px 16px rgba(220,38,38,0.3)',
            transition: 'transform 0.13s, box-shadow 0.13s',
            opacity: joining ? 0.7 : 1,
            alignSelf: 'center',
          }}
          onMouseEnter={(e) => { if (!joining) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(220,38,38,0.4)'; }}}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(220,38,38,0.3)'; }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          {joining ? 'Connecting…' : 'Create & Join'}
        </button>

      </div>

      {/* ── RIGHT PANEL ────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: '#f1f2f6' }}>

        {/* Gradient header */}
        <div style={{
          background: 'linear-gradient(135deg,#3730a3 0%,#4338ca 40%,#4f46e5 70%,#6366f1 100%)',
          padding: '24px 28px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'relative', flexShrink: 0,
        }}>
          <div style={{ position: 'absolute', top: -30, right: 80, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -20, right: 20, width: 90, height: 90, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />

          <div style={{ flex: 1, textAlign: 'center' }}><LiveClock /></div>

          {/* Notification bell */}
          <div ref={notifRef} style={{ position: 'relative', flexShrink: 0 }}>
            <button
              onClick={() => setShowNotifs((v) => !v)}
              style={{
                position: 'relative', background: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.25)', borderRadius: 10,
                width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', cursor: 'pointer', transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
              title="Meeting notifications"
            >
              <BellIcon size={18} />
              {unreadCount > 0 && (
                <span style={{
                  position: 'absolute', top: -4, right: -4,
                  background: '#ef4444', color: '#fff',
                  borderRadius: 10, minWidth: 17, height: 17,
                  fontSize: 10, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px',
                }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
            </button>
            {showNotifs && (
              <div style={{
                position: 'absolute', top: 48, right: 0,
                background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14,
                boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
                width: 330, maxHeight: 380, overflowY: 'auto', zIndex: 1000,
              }}>
                <div style={{ padding: '12px 16px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>Meeting Reminders</span>
                  {unreadCount > 0 && (
                    <button onClick={markAllRead} style={{ fontSize: 11, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                      Mark all read
                    </button>
                  )}
                </div>
                {!notifsLoaded
                  ? <div style={{ padding: 16, color: '#94a3b8', fontSize: 12 }}>Loading…</div>
                  : notifs.length === 0
                    ? <div style={{ padding: 24, color: '#94a3b8', fontSize: 12.5, textAlign: 'center' }}>No notifications yet.</div>
                    : notifs.map((n) => (
                        <div
                          key={n.notif_id} onClick={() => markRead(n.notif_id)}
                          style={{
                            padding: '10px 14px', cursor: 'pointer',
                            background: n.is_read ? 'transparent' : 'rgba(99,102,241,0.05)',
                            borderBottom: '1px solid #f8fafc', transition: 'background 0.1s',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = n.is_read ? 'transparent' : 'rgba(99,102,241,0.05)'; }}
                        >
                          <div style={{ fontSize: 12.5, color: n.is_read ? '#64748b' : '#1e293b', lineHeight: 1.5 }}>{n.message}</div>
                          <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 4 }}>
                            {new Date(n.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            {!n.is_read && <span style={{ color: '#6366f1', marginLeft: 6, fontWeight: 700 }}>● New</span>}
                          </div>
                        </div>
                      ))
                }
              </div>
            )}
          </div>
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 4, padding: '14px 20px 0', flexWrap: 'wrap', flexShrink: 0, alignItems: 'center' }}>
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              style={{
                padding: '7px 16px', borderRadius: 20, border: 'none',
                background: activeTab === t.key ? '#4f46e5' : '#fff',
                color: activeTab === t.key ? '#fff' : '#374151',
                fontSize: 13, fontWeight: activeTab === t.key ? 700 : 500,
                cursor: 'pointer', transition: 'background 0.15s, color 0.15s',
                boxShadow: activeTab === t.key ? '0 2px 8px rgba(79,70,229,0.3)' : '0 1px 3px rgba(0,0,0,0.07)',
              }}
            >{t.label}</button>
          ))}
          {isAdmin && (
            <button
              onClick={() => { setSchedForm({ title: '', date: '', time: '', note: '' }); setInvitedUsers([]); setShowSchedule(true); }}
              style={{
                marginLeft: 'auto', padding: '7px 16px', borderRadius: 20,
                border: '1.5px solid #6366f1', background: 'transparent',
                color: '#6366f1', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', transition: 'background 0.15s',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <CalendarIcon size={14} /> + Schedule
            </button>
          )}
        </div>

        {/* Schedule list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px 20px' }}>
          {schedLoading ? (
            <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', marginTop: 40 }}>Loading…</div>
          ) : displayedSchedules.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 260, gap: 12 }}>
              <svg width="180" height="130" viewBox="0 0 200 140" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.65 }}>
                <rect x="60" y="25" width="80" height="58" rx="6" fill="#c7d2fe" stroke="#a5b4fc" strokeWidth="2"/>
                <rect x="66" y="31" width="68" height="44" rx="3" fill="#e0e7ff"/>
                <circle cx="88" cy="47" r="8" fill="#818cf8"/>
                <circle cx="112" cy="47" r="8" fill="#6366f1"/>
                <rect x="92" y="83" width="12" height="14" rx="2" fill="#a5b4fc"/>
                <rect x="80" y="95" width="40" height="5" rx="2.5" fill="#c7d2fe"/>
              </svg>
              <p style={{ margin: 0, fontSize: 14, color: '#6b7280', fontWeight: 600 }}>
                {activeTab === 'hosted' ? 'You have not scheduled any meetings yet.' :
                 activeTab === 'invited' ? 'No meeting invitations yet.' :
                 activeTab === 'today' ? 'No meetings scheduled for today.' :
                 activeTab === 'tomorrow' ? 'No meetings scheduled for tomorrow.' :
                 'No meetings scheduled.'}
              </p>
              {isAdmin && activeTab !== 'invited' && (
                <button
                  onClick={() => { setSchedForm({ title: '', date: '', time: '', note: '' }); setInvitedUsers([]); setShowSchedule(true); }}
                  style={{ marginTop: 4, padding: '8px 20px', borderRadius: 8, border: '1.5px solid #6366f1', background: 'transparent', color: '#6366f1', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  + Schedule a Meeting
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {displayedSchedules.map((s) => {
                const invCount = s.invited_usernames
                  ? s.invited_usernames.split(',').filter(Boolean).length : 0;
                const isPast = new Date(s.scheduled_at) < new Date();
                return (
                  <div key={s.schedule_id} style={{
                    background: '#fff', border: `1px solid ${isPast ? '#f1f5f9' : '#e8eaf0'}`,
                    borderRadius: 10, padding: '12px 16px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                    opacity: isPast ? 0.72 : 1,
                  }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: '#1e293b', fontWeight: 700, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.title}
                      </div>
                      <div style={{ color: '#64748b', fontSize: 12, marginTop: 3 }}>
                        <CalendarIcon size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        {fmtDateTime(s.scheduled_at)}
                        {s.created_by_name && <span style={{ marginLeft: 6 }}>· {s.created_by_name}</span>}
                        {invCount > 0 && <span style={{ color: '#6366f1', marginLeft: 6 }}>· {invCount} invited</span>}
                      </div>
                      {s.note && <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 3, fontStyle: 'italic' }}>{s.note}</div>}
                      {isPast && <div style={{ fontSize: 10, color: '#f59e0b', marginTop: 3, fontWeight: 600 }}>● Ended</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button
                        onClick={() => handleStartMeeting(s.title)}
                        style={{
                          background: isPast ? '#e2e8f0' : '#4f46e5',
                          color: isPast ? '#94a3b8' : '#fff',
                          border: 'none', borderRadius: 20,
                          padding: '6px 18px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={(e) => { if (!isPast) e.currentTarget.style.background = '#4338ca'; }}
                        onMouseLeave={(e) => { if (!isPast) e.currentTarget.style.background = '#4f46e5'; }}
                      >
                        {isPast ? 'Re-join' : 'Start'}
                      </button>
                      {s.created_by_me && (
                        <button
                          onClick={() => deleteSchedule(s.schedule_id)}
                          style={{
                            background: 'rgba(239,68,68,0.08)', color: '#f87171',
                            border: '1px solid rgba(239,68,68,0.2)', borderRadius: 7,
                            padding: '6px 10px', fontSize: 12, cursor: 'pointer', lineHeight: 1,
                          }}
                          title="Delete schedule"
                        >✕</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Schedule Modal ────────────────────────────────────── */}
      {showSchedule && isAdmin && (
        <div style={modalOverlay} onClick={() => { setShowSchedule(false); setWantSchedule(false); }}>
          <div style={{ ...modalBox, maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#1e293b' }}>
              Schedule a Meeting
            </div>

            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', top: -9, left: 10, background: '#fff', padding: '0 4px', fontSize: 11, fontWeight: 700, color: '#6366f1' }}>Title *</span>
              <input autoFocus value={schedForm.title}
                onChange={(e) => setSchedForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Staff Weekly Sync"
                style={{ ...inp }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#6366f1'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
              />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <span style={{ position: 'absolute', top: -9, left: 10, background: '#fff', padding: '0 4px', fontSize: 11, fontWeight: 700, color: '#6366f1' }}>Date *</span>
                <input type="date" value={schedForm.date} lang="en-GB"
                  onChange={(e) => setSchedForm((p) => ({ ...p, date: e.target.value }))}
                  style={{ ...inp }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = '#6366f1'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
                />
              </div>
              <div style={{ flex: 1, position: 'relative' }}>
                <span style={{ position: 'absolute', top: -9, left: 10, background: '#fff', padding: '0 4px', fontSize: 11, fontWeight: 700, color: '#6366f1' }}>Time *</span>
                <input type="time" value={schedForm.time}
                  onChange={(e) => setSchedForm((p) => ({ ...p, time: e.target.value }))}
                  style={{ ...inp }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = '#6366f1'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
                />
              </div>
            </div>

            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', top: -9, left: 10, background: '#fff', padding: '0 4px', fontSize: 11, fontWeight: 700, color: '#6366f1' }}>Agenda / Notes</span>
              <input value={schedForm.note}
                onChange={(e) => setSchedForm((p) => ({ ...p, note: e.target.value }))}
                placeholder="Optional agenda (optional)"
                style={{ ...inp }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#6366f1'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
              />
            </div>

            {/* Invite staff */}
            <div>
              <div style={{ fontSize: 11, color: '#6366f1', fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Invite Staff {invitedUsers.length > 0 && `(${invitedUsers.length} selected)`}
              </div>
              {staffList.length === 0
                ? <div style={{ color: '#94a3b8', fontSize: 12 }}>No staff accounts found.</div>
                : (
                  <div style={{ maxHeight: 160, overflowY: 'auto', border: '1.5px solid #e2e8f0', borderRadius: 8, background: '#f8fafc' }}>
                    {staffList.map((staff) => {
                      const selected = invitedUsers.includes(staff.username);
                      return (
                        <div
                          key={staff.username}
                          onClick={() => toggleInvite(staff.username)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                            cursor: 'pointer',
                            background: selected ? 'rgba(99,102,241,0.07)' : 'transparent',
                            borderBottom: '1px solid #f1f5f9', transition: 'background 0.1s',
                          }}
                          onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = '#f1f5f9'; }}
                          onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = selected ? 'rgba(99,102,241,0.07)' : 'transparent'; }}
                        >
                          <div style={{
                            width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                            border: selected ? '2px solid #6366f1' : '2px solid #cbd5e1',
                            background: selected ? '#6366f1' : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {selected && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><polyline points="1.5,5 4,7.5 8.5,2.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                          </div>
                          <div style={{
                            width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                            background: tileColor(staff.full_name || staff.username),
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, fontWeight: 700, color: '#fff',
                          }}>
                            {initialsOf(staff.full_name || staff.username)}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: selected ? '#4f46e5' : '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {staff.full_name || staff.username}
                            </div>
                            <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {staff.email || `@${staff.username}`}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              }
              {invitedUsers.length > 0 && (
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                  Invitees will receive a notification in their account.
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 4 }}>
              <button
                onClick={() => { setShowSchedule(false); setWantSchedule(false); }}
                style={{ padding: '10px 20px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: 'transparent', color: '#64748b', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}
              >Cancel</button>
              <button
                onClick={saveSchedule} disabled={!canSave}
                style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: canSave ? '#4f46e5' : '#e2e8f0', color: canSave ? '#fff' : '#94a3b8', fontSize: 13, fontWeight: 700, cursor: canSave ? 'pointer' : 'not-allowed', fontFamily: 'inherit', transition: 'background 0.15s' }}
              >
                {schedSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN MEETING COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function Meeting() {
  // ── Identity — read once from sessionStorage (set by App.js on login) ───────
  const identity = useMemo(getIdentity, []);
  const { username: meUsername, displayName: myName } = identity;

  // ── Room state ────────────────────────────────────────────────────────────────
  const [inRoom,       setInRoom]       = useState(false);
  const [joining,      setJoining]      = useState(false);
  const [joinErr,      setJoinErr]      = useState('');
  const [roomTitle,    setRoomTitle]    = useState('');

  // ── AV state ─────────────────────────────────────────────────────────────────
  const [isMuted,      setIsMuted]      = useState(false);
  const [isVideoOn,    setIsVideoOn]    = useState(false);
  const [isSharing,    setIsSharing]    = useState(false);
  const [localVideo,   setLocalVideo]   = useState(null);

  // ── Participants ──────────────────────────────────────────────────────────────
  const [participants, setParticipants] = useState([]);
  const [speaking,     setSpeaking]     = useState({});
  const [allStaff,     setAllStaff]     = useState([]);
  const [callSent,     setCallSent]     = useState({});

  // ── Panels ────────────────────────────────────────────────────────────────────
  const [chatOpen,     setChatOpen]     = useState(false);
  const [peopleOpen,   setPeopleOpen]   = useState(false);

  // ── Chat ─────────────────────────────────────────────────────────────────────
  const [messages,     setMessages]     = useState([]);
  const [draft,        setDraft]        = useState('');
  const [sending,      setSending]      = useState(false);
  const [chatErr,      setChatErr]      = useState('');
  const [unread,       setUnread]       = useState(0);
  const [allUsers,     setAllUsers]     = useState([]);
  const [mentionQ,     setMentionQ]     = useState('');
  const [showMention,  setShowMention]  = useState(false);

  // ── Duration timer ────────────────────────────────────────────────────────────
  const [joinedAt,     setJoinedAt]     = useState(null);
  const [elapsed,      setElapsed]      = useState('');

  // ── Refs ──────────────────────────────────────────────────────────────────────
  const localStreamRef    = useRef(null);
  const localVideoRef     = useRef(null);
  const screenStreamRef   = useRef(null);
  const pcsRef            = useRef({});
  const audioElemsRef     = useRef({});
  const analyserRefs      = useRef({});
  const lastSigIdRef      = useRef(0);
  const lastMsgIdRef      = useRef(0);
  const heartbeatRef      = useRef(null);
  const presencePollRef   = useRef(null);
  const sigPollRef        = useRef(null);
  const msgPollRef        = useRef(null);
  const elapsedRef        = useRef(null);
  const chatEndRef        = useRef(null);
  const draftRef          = useRef(null);
  const isMutedRef        = useRef(false);
  const prevPartsRef      = useRef([]);
  isMutedRef.current      = isMuted;

  // Load all users for @mention (once)
  useEffect(() => {
    usersAPI.getAll().then((r) => setAllUsers(r.data?.data || [])).catch(() => {});
  }, []);

  // Load staff for People panel when in room
  useEffect(() => {
    if (!inRoom) return;
    meetingAPI.getStaff().then((r) => setAllStaff(r.data?.data || [])).catch(() => {});
  }, [inRoom]);

  // Duration timer
  useEffect(() => {
    if (!joinedAt) { setElapsed(''); return; }
    const tick = () => {
      const s = Math.floor((Date.now() - joinedAt) / 1000);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      setElapsed(h > 0
        ? `${h}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`
        : `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`
      );
    };
    tick();
    elapsedRef.current = setInterval(tick, 1000);
    return () => clearInterval(elapsedRef.current);
  }, [joinedAt]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, chatOpen]);

  // ── Speaking detection ────────────────────────────────────────────────────────
  const startSpeakDetect = useCallback((username, stream) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream);
      const ana = ctx.createAnalyser();
      ana.fftSize = 256;
      src.connect(ana);
      const buf = new Uint8Array(ana.frequencyBinCount);
      let raf;
      const tick = () => {
        ana.getByteFrequencyData(buf);
        const avg = buf.reduce((a, b) => a + b, 0) / buf.length;
        setSpeaking((prev) => {
          const was = prev[username] || false;
          const now = avg > 10;
          return was === now ? prev : { ...prev, [username]: now };
        });
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
      analyserRefs.current[username] = { ctx, raf };
    } catch { /**/ }
  }, []);

  const stopSpeakDetect = useCallback((username) => {
    const a = analyserRefs.current[username];
    if (a) {
      cancelAnimationFrame(a.raf);
      try { a.ctx.close(); } catch { /**/ }
      delete analyserRefs.current[username];
    }
    setSpeaking((prev) => { const n = { ...prev }; delete n[username]; return n; });
  }, []);

  // ── WebRTC helpers ────────────────────────────────────────────────────────────
  const getOrCreatePC = useCallback((remoteUser) => {
    if (pcsRef.current[remoteUser]) return pcsRef.current[remoteUser];
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    pc.onicecandidate = (e) => {
      if (e.candidate)
        meetingAPI.signal(remoteUser, 'ice', JSON.stringify(e.candidate)).catch(() => {});
    };

    pc.ontrack = (e) => {
      let el = audioElemsRef.current[remoteUser];
      if (!el) {
        el = document.createElement('audio');
        el.autoplay = true;
        document.body.appendChild(el);
        audioElemsRef.current[remoteUser] = el;
      }
      el.srcObject = e.streams[0];
      startSpeakDetect(remoteUser, e.streams[0]);
    };

    if (localStreamRef.current)
      localStreamRef.current.getTracks().forEach((t) => pc.addTrack(t, localStreamRef.current));

    pcsRef.current[remoteUser] = pc;
    return pc;
  }, [startSpeakDetect]);

  const destroyPC = useCallback((remoteUser) => {
    stopSpeakDetect(remoteUser);
    const pc = pcsRef.current[remoteUser];
    if (pc) { try { pc.close(); } catch { /**/ } delete pcsRef.current[remoteUser]; }
    const el = audioElemsRef.current[remoteUser];
    if (el) { try { el.srcObject = null; el.remove(); } catch { /**/ } delete audioElemsRef.current[remoteUser]; }
  }, [stopSpeakDetect]);

  const callPeer = useCallback(async (remoteUser) => {
    if (remoteUser === meUsername) return;
    try {
      const pc = getOrCreatePC(remoteUser);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await meetingAPI.signal(remoteUser, 'offer', JSON.stringify(offer));
    } catch { /**/ }
  }, [meUsername, getOrCreatePC]);

  // ── Signal polling ────────────────────────────────────────────────────────────
  const pollSignals = useCallback(async () => {
    try {
      const res  = await meetingAPI.signals(lastSigIdRef.current);
      const list = res.data?.data || [];
      for (const sig of list) {
        lastSigIdRef.current = sig.signal_id;
        const from = sig.from_user;
        let parsed;
        try { parsed = JSON.parse(sig.payload); } catch { continue; }
        if (sig.signal_type === 'offer') {
          const pc = getOrCreatePC(from);
          await pc.setRemoteDescription(new RTCSessionDescription(parsed));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          await meetingAPI.signal(from, 'answer', JSON.stringify(answer));
        } else if (sig.signal_type === 'answer') {
          const pc = pcsRef.current[from];
          if (pc) await pc.setRemoteDescription(new RTCSessionDescription(parsed));
        } else if (sig.signal_type === 'ice') {
          const pc = pcsRef.current[from];
          if (pc) { try { await pc.addIceCandidate(new RTCIceCandidate(parsed)); } catch { /**/ } }
        }
      }
    } catch { /**/ }
  }, [getOrCreatePC]);

  // ── Presence polling ──────────────────────────────────────────────────────────
  const pollPresence = useCallback(async () => {
    try {
      const res  = await meetingAPI.presence();
      const list = res.data?.data || [];
      setParticipants(list);
      const prev = prevPartsRef.current.map((p) => p.username_lc);
      const curr = list.map((p) => p.username_lc);
      for (const u of curr.filter((u) => !prev.includes(u) && u !== meUsername)) await callPeer(u);
      for (const u of prev.filter((u) => !curr.includes(u))) destroyPC(u);
      prevPartsRef.current = list;
    } catch { /**/ }
  }, [meUsername, callPeer, destroyPC]);

  // ── Chat polling ──────────────────────────────────────────────────────────────
  const pollMessages = useCallback(async () => {
    try {
      const res  = await chatAPI.getMessages(THREAD_KEY, lastMsgIdRef.current);
      const list = res.data?.data || [];
      if (list.length > 0) {
        setMessages((prev) => [...prev, ...list]);
        lastMsgIdRef.current = list[list.length - 1].message_id;
        if (!chatOpen)
          setUnread((n) => n + list.filter((m) => (m.sender_username || '').toLowerCase() !== meUsername).length);
      }
    } catch { /**/ }
  }, [meUsername, chatOpen]);

  // ── Join room ─────────────────────────────────────────────────────────────────
  const joinRoom = async ({ title, meetingWith: _mw, message: _msg } = {}) => {
    setJoining(true);
    setJoinErr('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;
      startSpeakDetect(meUsername, stream);

      // Load existing chat history
      const res  = await chatAPI.getMessages(THREAD_KEY, 0);
      const list = res.data?.data || [];
      setMessages(list);
      lastMsgIdRef.current = list.length ? list[list.length - 1].message_id : 0;

      await meetingAPI.heartbeat(false);
      setRoomTitle(title || 'Meeting Room');
      setJoinedAt(Date.now());
      setInRoom(true);

      heartbeatRef.current    = setInterval(() => meetingAPI.heartbeat(isMutedRef.current).catch(() => {}), 5000);
      presencePollRef.current = setInterval(pollPresence, 3000);
      sigPollRef.current      = setInterval(pollSignals,  1500);
      msgPollRef.current      = setInterval(pollMessages, 3000);
      pollPresence();
    } catch (err) {
      setJoinErr(
        err.name === 'NotAllowedError' || (err.message || '').includes('Permission')
          ? 'Microphone access denied. Please allow mic access in your browser and try again.'
          : `Could not join: ${err.message}`
      );
    } finally {
      setJoining(false);
    }
  };

  // ── Leave room ────────────────────────────────────────────────────────────────
  const leaveRoom = useCallback(() => {
    clearInterval(heartbeatRef.current);
    clearInterval(presencePollRef.current);
    clearInterval(sigPollRef.current);
    clearInterval(msgPollRef.current);
    clearInterval(elapsedRef.current);
    Object.keys(pcsRef.current).forEach(destroyPC);
    stopSpeakDetect(meUsername);
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (localVideoRef.current) {
      localVideoRef.current.getTracks().forEach((t) => t.stop());
      localVideoRef.current = null;
    }
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((t) => t.stop());
      screenStreamRef.current = null;
    }
    setLocalVideo(null);
    setIsVideoOn(false);
    setIsSharing(false);
    meetingAPI.leave().catch(() => {});
    prevPartsRef.current = [];
    lastSigIdRef.current = 0;
    setInRoom(false);
    setParticipants([]);
    setSpeaking({});
    setJoinedAt(null);
    setUnread(0);
    setMessages([]);
    setChatOpen(false);
    setPeopleOpen(false);
  }, [destroyPC, stopSpeakDetect, meUsername]);

  // Cleanup on unmount
  useEffect(() => () => { if (inRoom) leaveRoom(); }, []); // eslint-disable-line

  // ── Toggle mute ───────────────────────────────────────────────────────────────
  const toggleMute = () => {
    const next = !isMuted;
    setIsMuted(next);
    if (localStreamRef.current)
      localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = !next; });
  };

  // ── Toggle camera ─────────────────────────────────────────────────────────────
  const toggleVideo = async () => {
    if (isVideoOn) {
      if (localVideoRef.current) {
        localVideoRef.current.getTracks().forEach((t) => t.stop());
        localVideoRef.current = null;
      }
      setLocalVideo(null);
      setIsVideoOn(false);
    } else {
      try {
        const vStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        localVideoRef.current = vStream;
        setLocalVideo(vStream);
        setIsVideoOn(true);
      } catch (err) {
        alert(
          err.name === 'NotAllowedError'
            ? 'Camera access denied. Please allow camera permission and try again.'
            : `Could not start camera: ${err.message}`
        );
      }
    }
  };

  // ── Toggle screen share ───────────────────────────────────────────────────────
  const toggleShare = async () => {
    if (isSharing) {
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach((t) => t.stop());
        screenStreamRef.current = null;
      }
      setIsSharing(false);
    } else {
      try {
        const sStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
        screenStreamRef.current = sStream;
        setIsSharing(true);
        // Stop sharing when user closes the OS picker
        sStream.getVideoTracks()[0].onended = () => {
          screenStreamRef.current = null;
          setIsSharing(false);
        };
      } catch { /* user cancelled */ }
    }
  };

  // ── Send call invite ──────────────────────────────────────────────────────────
  const sendCallInvite = async (username) => {
    if (callSent[username]) return;
    try {
      await meetingAPI.callInvite(username);
      setCallSent((prev) => ({ ...prev, [username]: true }));
      setTimeout(() => setCallSent((prev) => { const n = { ...prev }; delete n[username]; return n; }), 5000);
    } catch { /**/ }
  };

  // ── Panel controls ────────────────────────────────────────────────────────────
  const openChat   = () => { setChatOpen(true);  setPeopleOpen(false); setUnread(0); };
  const openPeople = () => { setPeopleOpen(true); setChatOpen(false); };
  const closePanel = () => { setChatOpen(false);  setPeopleOpen(false); };

  // ── Send chat message ─────────────────────────────────────────────────────────
  const sendMessage = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setChatErr('');
    try {
      const res = await chatAPI.send(THREAD_KEY, text, THREAD_TITLE);
      const m   = res.data?.data;
      if (m) { setMessages((prev) => [...prev, m]); lastMsgIdRef.current = m.message_id; }
      setDraft('');
      setShowMention(false);
    } catch (err) {
      setChatErr(err.response?.data?.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleDraftChange = (e) => {
    const val = e.target.value;
    setDraft(val);
    const m = val.match(/@(\w*)$/);
    if (m) { setMentionQ(m[1]); setShowMention(true); }
    else   { setShowMention(false); }
  };

  const handleKey = (e) => {
    if (showMention && e.key === 'Escape') { setShowMention(false); return; }
    if (!showMention && e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const insertMention = (username) => {
    setDraft(draft.replace(/@(\w*)$/, `@${username} `));
    setShowMention(false);
    draftRef.current?.focus();
  };

  const mentionSuggestions = useMemo(() => {
    if (!showMention) return [];
    const q = mentionQ.toLowerCase();
    return allUsers.filter((u) =>
      !q || (u.username || '').toLowerCase().includes(q) || (u.full_name || '').toLowerCase().includes(q)
    ).slice(0, 6);
  }, [mentionQ, showMention, allUsers]);

  // ── Home screen ───────────────────────────────────────────────────────────────
  if (!inRoom) {
    return (
      <HomeScreen
        onNewMeeting={joinRoom}
        joining={joining}
        error={joinErr}
        identity={identity}
      />
    );
  }

  const panelOpen = chatOpen || peopleOpen;

  // ── IN-MEETING LAYOUT ─────────────────────────────────────────────────────────
  return (
    <div style={{
      display: 'flex',
      height: 'calc(100vh - 120px)',
      minHeight: 520,
      background: '#0b0b14',
      borderRadius: 12,
      overflow: 'hidden',
      position: 'relative',
      fontFamily: 'inherit',
    }}>

      {/* ── Main area (stage + toolbar) ────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        {/* Top bar */}
        <div style={{
          height: 54, background: '#0f0f1e',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 8px #22c55e', flexShrink: 0 }} />
              <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 14 }}>
                {roomTitle || 'Meeting Room'}
              </span>
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {elapsed && (
              <span style={{ color: '#64748b', fontSize: 12, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                {elapsed}
              </span>
            )}
            <span style={{ color: '#64748b', fontSize: 12 }}>
              {participants.length} participant{participants.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Stage */}
        <div style={{
          flex: 1,
          display: 'flex', flexWrap: 'wrap', gap: 12, padding: 16,
          alignContent: 'flex-start', alignItems: 'flex-start',
          overflowY: 'auto', background: '#0d0d1a',
        }}>
          {participants.length === 0 ? (
            <div style={{
              width: '100%', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', minHeight: 260,
              color: '#475569', gap: 14,
            }}>
              <div style={{ fontSize: 52, opacity: 0.45 }}>🎙️</div>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Waiting for others to join…</p>
              <p style={{ margin: 0, fontSize: 12, color: '#334155' }}>
                Use the People panel to invite colleagues to this call
              </p>
            </div>
          ) : (
            participants.map((p) => (
              <ParticipantTile
                key={p.username_lc}
                name={p.display_name}
                isMe={p.username_lc === meUsername}
                isMuted={p.is_muted}
                speaking={!p.is_muted && (speaking[p.username_lc] || false)}
                videoStream={p.username_lc === meUsername && isVideoOn ? localVideo : null}
              />
            ))
          )}
        </div>

        {/* Bottom toolbar */}
        <div style={{
          height: 84, background: '#0f0f1e',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 8, flexShrink: 0, padding: '0 12px',
        }}>
          <ToolbarBtn
            active={!isMuted} danger={isMuted}
            onClick={toggleMute}
            title={isMuted ? 'Unmute microphone' : 'Mute microphone'}
            label={isMuted ? 'Unmute' : 'Mute'}
            icon={<MicIcon off={isMuted} size={20} />}
          />
          <ToolbarBtn
            active={isVideoOn} danger={!isVideoOn}
            onClick={toggleVideo}
            title={isVideoOn ? 'Turn off camera' : 'Turn on camera'}
            label={isVideoOn ? 'Stop Video' : 'Start Video'}
            icon={<VideoIcon off={!isVideoOn} size={20} />}
          />
          <ToolbarBtn
            active={isSharing} danger={false}
            onClick={toggleShare}
            title={isSharing ? 'Stop sharing screen' : 'Share your screen'}
            label={isSharing ? 'Stop Share' : 'Share'}
            icon={<ScreenShareIcon active={isSharing} size={20} />}
          />

          {/* Leave — centre red button */}
          <button
            onClick={leaveRoom}
            title="Leave meeting"
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 4, width: 68, height: 58, borderRadius: 12, border: 'none',
              background: '#dc2626', color: '#fff', cursor: 'pointer',
              fontSize: 11, fontWeight: 700, margin: '0 4px',
              boxShadow: '0 2px 10px rgba(220,38,38,0.45)',
              transition: 'background 0.15s, box-shadow 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#b91c1c'; e.currentTarget.style.boxShadow = '0 4px 18px rgba(220,38,38,0.6)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.boxShadow = '0 2px 10px rgba(220,38,38,0.45)'; }}
          >
            <PhoneOffIcon size={20} />
            Leave
          </button>

          <ToolbarBtn
            active={chatOpen}
            onClick={chatOpen ? closePanel : openChat}
            title="Meeting chat"
            label="Chat"
            badge={!chatOpen && unread > 0 ? unread : 0}
            icon={<ChatBubbleIcon size={20} />}
          />
          <ToolbarBtn
            active={peopleOpen}
            onClick={peopleOpen ? closePanel : openPeople}
            title="Participants"
            label="People"
            icon={<PeopleIcon size={20} />}
          />
        </div>
      </div>

      {/* ── Side panel ─────────────────────────────────────────── */}
      <div style={{
        width: panelOpen ? 320 : 0,
        minWidth: 0,
        transition: 'width 0.22s ease',
        overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        background: '#13131f',
        borderLeft: panelOpen ? '1px solid rgba(255,255,255,0.07)' : 'none',
        flexShrink: 0,
      }}>

        {/* Chat panel */}
        {chatOpen && (
          <>
            <PanelHeader title="Meeting Chat" onClose={closePanel} />

            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>
              {messages.length === 0
                ? <div style={{ color: '#475569', textAlign: 'center', marginTop: 48, fontSize: 13 }}>
                    No messages yet — say hello 👋
                  </div>
                : messages.map((m) => (
                    <ChatBubble key={m.message_id} m={m} meUsername={meUsername} />
                  ))
              }
              <div ref={chatEndRef} />
            </div>

            {chatErr && (
              <div style={{ padding: '6px 14px', background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 12 }}>
                {chatErr}
              </div>
            )}

            {/* @mention dropdown */}
            {showMention && mentionSuggestions.length > 0 && (
              <div style={{ background: '#1e1e2e', borderTop: '1px solid rgba(255,255,255,0.07)', maxHeight: 180, overflowY: 'auto' }}>
                {mentionSuggestions.map((u) => (
                  <div
                    key={u.user_id}
                    onClick={() => insertMention(u.username)}
                    style={{ padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#2d2d3e'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{
                      width: 30, height: 30, borderRadius: '50%',
                      background: tileColor(u.full_name || u.username),
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0,
                    }}>
                      {initialsOf(u.full_name || u.username)}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>{u.full_name || u.username}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>@{u.username}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Composer */}
            <div style={{ padding: '10px 12px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <input
                ref={draftRef}
                type="text"
                placeholder="Message…  @ to mention"
                value={draft}
                onChange={handleDraftChange}
                onKeyDown={handleKey}
                disabled={sending}
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: 22,
                  border: '1px solid rgba(255,255,255,0.12)',
                  background: '#1e1e2e', color: '#f1f5f9',
                  fontSize: 13, outline: 'none', fontFamily: 'inherit',
                }}
              />
              <button
                onClick={sendMessage}
                disabled={!draft.trim() || sending}
                style={{
                  background: !draft.trim() || sending ? '#1e1e2e' : '#4f46e5',
                  color: !draft.trim() || sending ? '#374151' : '#fff',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '50%', width: 40, height: 40, flexShrink: 0,
                  cursor: !draft.trim() || sending ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background 0.15s',
                }}
                title="Send"
              >
                <SendIcon size={16} />
              </button>
            </div>
          </>
        )}

        {/* People panel */}
        {peopleOpen && (
          <>
            <PanelHeader title={`Participants (${participants.length})`} onClose={closePanel} />
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>

              {/* In-room section */}
              {participants.length > 0 && (
                <div style={{ padding: '8px 16px 4px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                  In this call · {participants.length}
                </div>
              )}
              {participants.length === 0 && (
                <div style={{ color: '#475569', textAlign: 'center', padding: '20px 16px', fontSize: 13 }}>
                  No one else in the call yet.
                </div>
              )}
              {participants.map((p) => {
                const isMe = p.username_lc === meUsername;
                return (
                  <div key={p.username_lc} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '9px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)',
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%',
                      background: tileColor(p.display_name),
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0,
                      border: speaking[p.username_lc] ? '2px solid #22c55e' : isMe ? '2px solid #4f46e5' : '2px solid transparent',
                      transition: 'border-color 0.25s',
                    }}>
                      {initialsOf(p.display_name)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.display_name}{isMe ? ' (You)' : ''}
                      </div>
                      <div style={{ fontSize: 11, color: p.is_muted ? '#f87171' : '#22c55e', marginTop: 2 }}>
                        {p.is_muted ? '🔇 Muted' : '🎙️ Unmuted'}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* All staff — invite section */}
              {allStaff.length > 0 && (
                <>
                  <div style={{ padding: '16px 16px 4px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                    Invite to call
                  </div>
                  {allStaff.map((staff) => {
                    const inRoom = participants.some((p) => p.username_lc === staff.username.toLowerCase());
                    const sent   = callSent[staff.username.toLowerCase()];
                    if (staff.username.toLowerCase() === meUsername) return null;
                    return (
                      <StaffCallRow
                        key={staff.username}
                        staff={staff}
                        inRoom={inRoom}
                        sent={sent}
                        onCall={() => sendCallInvite(staff.username.toLowerCase())}
                      />
                    );
                  })}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
